import React, { useMemo } from 'react';
import { useLocale } from '../../context/LocaleContext';
import { format } from 'date-fns';
import useNames from '../../hooks/useNames';
import useMonthDrag from '../../hooks/useMonthDrag';
import { monthGrid, chipTitle, attendeeNames } from '../../domain/calendarGrid';
import StatusGlyph from './StatusGlyph';
import { categoryPaint } from './eventPaint';
import s from './MonthView.module.css';

// Month grid — the view MMS opens on.
//
// Whole weeks from the one the month's 1st falls in to the one its last day
// falls in, so four, five or six rows of seven. Leading and trailing days from
// the neighbouring months are greyed. Each cell lists its events compactly; a
// recorded attendance shows its status glyph, so a teacher can scan a month
// for lessons still to mark.
//
// ── The days and a chip's words are the core's (#748) ──────────────────────
//
// Which days the grid holds, what each cell writes, which are outside the
// month and which is today come from `monthGrid`, and a chip's title from
// `chipTitle` (`src/domain/calendarGrid.js`, `core/src/week.rs`), because both
// phones draw this grid from the same answers
// (`core/src/calendar_grid_vectors.json`). What stays here is drawing, the
// drag, and the three-chip cut, which is this cell's height.
//
// ── Two channels ───────────────────────────────────────────────────────────
//
// The chip's fill is the category's colour and attendance is `StatusGlyph`
// inside it (`categoryPaint` in eventPaint.js has the argument, #109, #130).
// A chip here is one line, so the category is in the colour and in the
// fallback label only: a named attendee wins the text. The Week · List chip
// prints the category's name under the time as well, because it has the room.
//
// An all-day event is an ordinary chip (#160): MMS draws a plain day-cell chip
// in the category fill with "All Day" where a timed chip prints "1:00 PM -
// 1:30 PM".

const CHIP = {
  plain: s.event,
  tinted: s.eventTinted,
};

// ── The Region's words (#534) ──────────────────────────────────────────────
//
// As MMS writes them for Germany: headers `Mo Di Mi Do Fr Sa So`, a month's
// first day `Okt` over `1`, chip times `16:00 - 16:30`. So the day names are
// the Region's short stand-alone names (`weekdayNames`, Sunday first, turned to
// begin where the studio's week does), and a first of the month carries its
// month's short name — `Oct 1` in English, as the Week · List view writes it.
//
// ── A chip dragged to another day (#571) ───────────────────────────────────
//
// Dropping an entry on another day reschedules that one entry to that day,
// then opens the edit window so the time can be changed, defaulting to the
// entry's existing time. The gesture is useMonthDrag.js, the day is `dayDrop`,
// and the write and the window are CalendarPage's (`onMoveEvent`). A cell's
// `data-date` is what the drag finds it by. What MMS's month drag does
// afterwards is unchecked. An all-day event moves like any other.

export default function MonthView({
  month, occurrences, studentsById, categoryColorFor, onEventClick, onDayClick, onMoveEvent,
}) {
  const { formatTime, weekStartsOn, weekdayNames, monthShortNames } = useLocale();
  const names = useNames();
  const { startMove, clickSuppressed, movingId, overDate } = useMonthDrag({ onMoveEvent });
  // Today as the browser's calendar has it.
  const today = format(new Date(), 'yyyy-MM-dd');
  // The Region's first day (#505): MMS's month runs Monday to Sunday for
  // United Kingdom and Sunday to Saturday for United States.
  const days = useMemo(
    () => monthGrid(format(month, 'yyyy-MM-dd'), weekStartsOn, today, weekdayNames, monthShortNames),
    [month, weekStartsOn, today, weekdayNames, monthShortNames],
  );
  // The header is the first week's day names.
  const dayNames = days.slice(0, 7).map((d) => d.weekday);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const o of occurrences) {
      if (!m.has(o.date)) m.set(o.date, []);
      m.get(o.date).push(o);
    }
    return m;
  }, [occurrences]);

  return (
    <>

      <div className={s.month}>
        <div className={s.head}>
          {dayNames.map((d) => (
            <div className={s.dow} key={d}>{d}</div>
          ))}
        </div>
        {Array.from({ length: days.length / 7 }, (_, r) => (
          <div className={s.row} key={r}>
            {days.slice(r * 7, r * 7 + 7).map((day) => {
              const iso = day.date;
              const list = byDate.get(iso) || [];
              const outside = !day.inMonth;
              const cellClass = iso === overDate ? s.cellDropTarget : outside ? s.cellOutside : s.cell;
              return (
                <div
                  key={iso}
                  data-date={iso}
                  className={cellClass}
                  onClick={() => { if (!clickSuppressed()) onDayClick?.(iso); }}
                >
                  <div className={day.isToday ? s.dayNumToday : s.dayNum}>
                    {day.label}
                  </div>
                  {list.slice(0, 3).map((o) => {
                    const attendees = o.studentIds.map((id) => {
                      const student = studentsById?.(id);
                      return student ? names.student(student) : null;
                    });
                    // A group is `2 students`, as MMS writes it (#749); a
                    // screen reader still hears who.
                    const title = chipTitle(attendees, o.categoryLabel);
                    const who = attendees.length > 1 ? attendeeNames(attendees) : '';
                    const paint = categoryPaint(o, categoryColorFor, CHIP);
                    return (
                      <button
                        key={o.id}
                        {...paint}
                        // The one being carried steps back while the cell
                        // under the pointer lights up.
                        data-moving={o.id === movingId ? '' : undefined}
                        onPointerDown={onMoveEvent ? (e) => startMove(o, e) : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!clickSuppressed()) onEventClick?.(o, e.currentTarget);
                        }}
                      >
                        <StatusGlyph status={o.status || 'unrecorded'} />
                        <span className={s.eventName}>
                          {o.allDay ? 'All Day' : formatTime(o.startTime)} {title}
                          {who ? <span className="sr-only">: {who}</span> : null}
                        </span>
                      </button>
                    );
                  })}
                  {list.length > 3 ? (
                    <div className={s.more}>+{list.length - 3} more</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
